import { grey } from '@mui/material/colors';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import useScopedCartActions from '../../../hooks/cartScope/useScopedCartActions.hooks';
import { selectIsAgentActionBlocked } from '../../../redux/features/agentMode/agentMode.selector';
import { ParsedMenuItem } from '../../../types/menu';
import { BLOCKED_AFFORDANCE_STYLE } from '../../../utils/agentModeAffordance';
import { CartItem, CartModifierGroup } from '../../../utils/cart';
import Colors from '../../../utils/color';
import { checkItemInCartItemTree } from '../../../utils/menu';
import { getMenuUnificationFeatureFlag } from '../../../utils/session-storage';
import { useDisplayPreference } from '../../Layout/DisplayPreferenceContext';
import { MOD_GROUP_ROW_INDENT_PX } from '../components/ModGroupRow';
import { sortAndFilterMenuItems } from '../util/helper';
import {
  buildGridFollowUpSets,
  describePickChanges,
  isSingleChoiceGroup,
  PickChange,
  RepeatedSlotSet,
} from '../util/repeatedSlots.utils';
import { SelectMenuItemModifier } from './modGroup.props';
import RepeatedSlotPanel, {
  actionStyle,
  panelStyles,
  SlotDefaults,
} from './RepeatedSlotPanel';
import ModGroup from './ModGroup';
import { HIDDEN_WHEN_CLOSED } from './ModGroupOpenContext';
import useRepeatedSlotsWrite, {
  RepeatedSlotChange,
} from './useRepeatedSlotsWrite.hooks';

/**
 * HITL-1726 — the pick-any grid (Layout A) for a per-unit family that takes
 * any number of picks per unit, e.g. HS Crowd Pleaser #16's
 * `other_options_selection_burger_1..8`.
 *
 * - One row per option. The option's name sits on its own line, on the
 *   drawer's rail, with an "all" toggle at its end. Under it there are N
 *   cells on exactly the panels' grid, so column N is unit N everywhere on
 *   the item.
 * - Click a cell to toggle it. A press-and-drag across cells (along a row or
 *   down a column) sets every crossed cell to the state the first one
 *   switched TO, as ONE write.
 * - "all" turns the row on for every unit, or off if every unit had it.
 * - A capped family refuses a tick past its max for that unit, and that
 *   unit's column flashes. A family with a minimum shows each short unit's
 *   count in red, and its name with `!`.
 * - Undo is multi-step. There is no Fill all.
 *
 * Every gesture is one useRepeatedSlotsWrite batch: a tick is `{ to }`, an
 * untick is `{ from }`, at the unit's own path.
 */

const FLASH_MS = 600;
const UNDO_LIMIT = 50;

type Picks = string[][];

const gridColumns = (count: number) => `repeat(${count}, minmax(0, 1fr))`;

const cellStyle = (on: boolean, isFlashing: boolean): React.CSSProperties => ({
  height: 24,
  minWidth: 0,
  boxSizing: 'border-box',
  borderRadius: 2,
  border: `1px solid ${grey[400]}`,
  backgroundColor: on || isFlashing ? Colors.alto : 'transparent',
  color: Colors.daintree,
  fontSize: '11px',
  fontWeight: 700,
  textAlign: 'center',
  lineHeight: '22px',
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'background-color 120ms ease',
});

const styles = {
  numberRow: {
    display: 'grid',
    gap: 2,
    fontSize: '9px',
    lineHeight: '11px',
    textAlign: 'center',
    color: Colors.silverChalice,
    marginTop: 4,
  } as React.CSSProperties,
  nameLine: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginTop: 4,
    borderLeft: `2px solid ${Colors.silverChalice}`,
    paddingLeft: MOD_GROUP_ROW_INDENT_PX - 2,
    fontSize: '11px',
    color: Colors.silverChalice,
  } as React.CSSProperties,
  cells: {
    display: 'grid',
    gap: 2,
    margin: '2px 0',
  } as React.CSSProperties,
  countRow: {
    display: 'grid',
    gap: 2,
    fontSize: '10px',
    lineHeight: '12px',
    textAlign: 'center',
    marginTop: 2,
  } as React.CSSProperties,
};

const allToggleStyle = (on: boolean): React.CSSProperties => ({
  marginLeft: 'auto',
  border: `1px solid ${grey[400]}`,
  borderRadius: 2,
  padding: '0 6px',
  fontSize: '10px',
  lineHeight: '14px',
  fontWeight: on ? 700 : 400,
  backgroundColor: on ? Colors.alto : 'transparent',
  color: Colors.daintree,
  cursor: 'pointer',
  userSelect: 'none',
});

interface RepeatedPickGridProps {
  cartItem: CartItem;
  slotSet: RepeatedSlotSet;
  selectMenuItemModifier: SelectMenuItemModifier;
}

const RepeatedPickGrid = ({
  cartItem,
  slotSet,
  selectMenuItemModifier,
}: RepeatedPickGridProps) => {
  const write = useRepeatedSlotsWrite(cartItem);
  const { isDraft } = useScopedCartActions();
  const isAgentActionBlocked = useSelector(selectIsAgentActionBlocked);
  const isReadOnly = isAgentActionBlocked && !isDraft;
  const { pref: displayPref } = useDisplayPreference();
  const isCaps = displayPref.cpCase === 'caps';

  const { groups, paths, stem, min, max } = slotSet;
  const unitCount = groups.length;

  const options: ParsedMenuItem[] = useMemo(() => {
    const menuItems = groups[0].menuItems;
    return getMenuUnificationFeatureFlag()
      ? Object.values(menuItems)
      : sortAndFilterMenuItems(menuItems);
  }, [groups]);
  const optionsById = useMemo(
    () => Object.fromEntries(options.map((option) => [option.itemId, option])),
    [options]
  );

  // Each unit's picks, read from the cart every render.
  const picks: Picks = paths.map((pathToItem) => {
    const cartModGroup = checkItemInCartItemTree({
      cartItem,
      pathToItem,
    }) as CartModifierGroup | null;
    return Object.values(cartModGroup?.selectedItems ?? {}).map(
      (item) => item.itemId
    );
  });
  const has = (unit: number, id: string) => picks[unit].includes(id);
  const totalPicks = picks.reduce(
    (sum, unitPicks) => sum + unitPicks.length,
    0
  );
  const isUnitShort = (unit: number) => picks[unit].length < min;
  // HITL-1728: each ticked option's follow-ups, under its row.
  const followUpsByOption = buildGridFollowUpSets(slotSet, picks, options);
  // A follow-up only blocks folding when it is required, has no menu default
  // to fill it, and a ticked unit's cell is still empty. With a default it
  // fills itself a microtask later, so it must not pop the grid open.
  const hasUnmetFollowUp = Array.from(followUpsByOption.values())
    .flat()
    .some(
      (followUp) =>
        followUp.isRequired &&
        followUp.paths.some((path, unit) => {
          if (!followUp.applicable?.[unit]) return false;
          const group = followUp.groups[unit];
          if (group.defaultSelectedItemIds?.length) return false;
          const cartModGroup = checkItemInCartItemTree({
            cartItem,
            pathToItem: path,
          }) as CartModifierGroup | null;
          return !Object.keys(cartModGroup?.selectedItems ?? {}).length;
        })
    );
  const isUnmet =
    (min > 0 && picks.some((_, unit) => isUnitShort(unit))) || hasUnmetFollowUp;

  const renderFollowUps = (optionId: string) =>
    (followUpsByOption.get(optionId) ?? []).map((followUp) =>
      followUp.groups.every(
        (group, unit) =>
          !followUp.applicable?.[unit] || isSingleChoiceGroup(group)
      ) ? (
        <RepeatedSlotPanel
          key={`${optionId}_${followUp.stem}`}
          cartItem={cartItem}
          slotSet={followUp}
          selectMenuItemModifier={selectMenuItemModifier}
          isFollowUp
        />
      ) : (
        // Not single-pick: each unit's ordinary nested row, still mounted.
        <div key={`${optionId}_${followUp.stem}_rows`}>
          {followUp.groups.map((group, unit) =>
            followUp.applicable?.[unit] ? (
              <ModGroup
                // eslint-disable-next-line react/no-array-index-key
                key={unit}
                cartItem={cartItem}
                modGroup={group}
                pathToItem={followUp.paths[unit]}
                selectMenuItemModifier={selectMenuItemModifier}
              />
            ) : null
          )}
        </div>
      )
    );

  // HITL-1727: the grid starts as one line. Opening it sticks for the cart
  // line; a required family with a short unit is never folded away.
  const [isExpanded, setIsExpanded] = useState(false);
  useEffect(() => setIsExpanded(false), [cartItem.cartItemId]);

  const [undoStack, setUndoStack] = useState<Picks[]>([]);
  useEffect(() => setUndoStack([]), [cartItem.cartItemId]);

  const [flashingUnits, setFlashingUnits] = useState<number[]>([]);
  const flashTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    },
    []
  );
  const flashUnits = (units: number[]) => {
    if (!units.length) return;
    if (flashTimer.current) clearTimeout(flashTimer.current);
    setFlashingUnits(units);
    flashTimer.current = setTimeout(() => setFlashingUnits([]), FLASH_MS);
  };

  /**
   * Writes `target` (each unit's picks) over the cart, one batch. Returns
   * whether anything was written; `recordUndo` false is Undo itself.
   */
  const commit = (target: Picks, { recordUndo = true } = {}): boolean => {
    const changes: RepeatedSlotChange[] = [];
    const described: PickChange[] = [];
    groups.forEach((modGroup, unit) => {
      const before = picks[unit];
      const after = target[unit];
      options.forEach((option) => {
        const was = before.includes(option.itemId);
        const is = after.includes(option.itemId);
        if (was === is) return;
        changes.push(
          is
            ? { modGroup, pathToItem: paths[unit], to: option }
            : { modGroup, pathToItem: paths[unit], from: option }
        );
        described.push({ unit, optionName: option.name, on: is });
      });
    });
    if (!changes.length) return false;
    if (!write(changes, describePickChanges(cartItem.name, described)))
      return false;
    setUndoStack((stack) =>
      recordUndo ? [...stack, picks].slice(-UNDO_LIMIT) : stack.slice(0, -1)
    );
    return true;
  };

  /**
   * `cells` set to `on`. A tick that would take a unit past its max is
   * refused, and that unit's column flashes.
   */
  const applyCells = (cells: { unit: number; id: string }[], on: boolean) => {
    const target = picks.map((unitPicks) => [...unitPicks]);
    const refused = new Set<number>();
    cells.forEach(({ unit, id }) => {
      const option = optionsById[id];
      if (!option || option.available === false) return;
      const unitPicks = target[unit];
      if (on) {
        if (unitPicks.includes(id)) return;
        if (unitPicks.length >= max) {
          refused.add(unit);
          return;
        }
        unitPicks.push(id);
      } else {
        target[unit] = unitPicks.filter((pick) => pick !== id);
      }
    });
    flashUnits(Array.from(refused));
    commit(target);
  };

  // Drag: the cells crossed so far and the state the first one switched to.
  const [stroke, setStroke] = useState<{
    on: boolean;
    cells: { unit: number; id: string }[];
  } | null>(null);

  const onCellPointerDown = (
    unit: number,
    id: string,
    event: React.PointerEvent
  ) => {
    if (isReadOnly || event.button > 0) return;
    if (optionsById[id]?.available === false) return;
    event.preventDefault();
    const target = event.target as Element;
    if (target.hasPointerCapture?.(event.pointerId)) {
      target.releasePointerCapture(event.pointerId);
    }
    setStroke({ on: !has(unit, id), cells: [{ unit, id }] });
  };

  const onCellPointerEnter = (unit: number, id: string) => {
    setStroke((current) =>
      current &&
      !current.cells.some((cell) => cell.unit === unit && cell.id === id)
        ? { ...current, cells: [...current.cells, { unit, id }] }
        : current
    );
  };

  const finishStrokeRef = useRef<(commitIt: boolean) => void>(() => {});
  finishStrokeRef.current = (commitIt: boolean) => {
    if (commitIt && stroke) applyCells(stroke.cells, stroke.on);
    setStroke(null);
  };
  const isStroking = stroke !== null;
  useEffect(() => {
    if (!isStroking) return undefined;
    const onUp = () => finishStrokeRef.current(true);
    const onCancel = () => finishStrokeRef.current(false);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, [isStroking]);

  const isRowAllOn = (id: string) => picks.every((_, unit) => has(unit, id));

  const onAllClick = (option: ParsedMenuItem) => {
    if (isReadOnly || option.available === false) return;
    const on = !isRowAllOn(option.itemId);
    applyCells(
      groups.map((_, unit) => ({ unit, id: option.itemId })),
      on
    );
  };

  const canUndo = !isReadOnly && undoStack.length > 0;
  const onUndo = () => {
    if (canUndo) commit(undoStack[undoStack.length - 1], { recordUndo: false });
  };

  const previewOn = (unit: number, id: string) =>
    stroke?.cells.some((cell) => cell.unit === unit && cell.id === id)
      ? stroke.on
      : has(unit, id);

  // A grid shown open because it was unmet stays open once met: folding it
  // away under the agent's last tick would be the panel moving mid-task.
  useEffect(() => {
    if (isUnmet) setIsExpanded(true);
  }, [isUnmet]);
  const isCollapsed = !isExpanded && !isUnmet;
  // `no pickles ×3, add mayo ×1` in option order; `none` when nothing ticked.
  const collapsedSummary =
    options
      .map((option) => ({
        name: option.name,
        count: picks.filter((unitPicks) => unitPicks.includes(option.itemId))
          .length,
      }))
      .filter(({ count }) => count > 0)
      .map(({ name, count }) => `${name} ×${count}`)
      .join(', ') || 'none';

  const limitText = Number.isFinite(max)
    ? ` (select up to ${max} each)`
    : ` (${totalPicks} picks)`;
  const labelColor = isUnmet ? Colors.Thunderbird : Colors.silverChalice;
  const readOnlyStyle = isReadOnly ? BLOCKED_AFFORDANCE_STYLE : null;

  return (
    <div
      style={{ ...panelStyles.root, ...readOnlyStyle }}
      data-testid="repeated-pick-grid"
    >
      <div style={panelStyles.body}>
        {groups.map((modGroup, unit) => (
          <SlotDefaults
            key={paths[unit]}
            cartItem={cartItem}
            modGroup={modGroup}
            pathToItem={paths[unit]}
            selectMenuItemModifier={selectMenuItemModifier}
          />
        ))}
        {isCollapsed ? (
          <div
            role="button"
            tabIndex={0}
            data-testid="repeated-pick-collapsed"
            style={panelStyles.collapsedRow}
            onClick={() => setIsExpanded(true)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ')
                setIsExpanded(true);
            }}
          >
            <span style={panelStyles.collapsedCaret}>›</span>
            <span
              style={{
                ...panelStyles.label,
                color: Colors.silverChalice,
                ...(isCaps ? { textTransform: 'uppercase' } : null),
              }}
            >
              {`${stem}: `}
              <span style={{ color: Colors.daintree, fontWeight: 400 }}>
                {collapsedSummary}
              </span>
            </span>
          </div>
        ) : (
          <>
            <div style={panelStyles.header}>
              <span
                data-testid="repeated-pick-label"
                style={{
                  ...panelStyles.label,
                  color: labelColor,
                  ...(isCaps ? { textTransform: 'uppercase' } : null),
                }}
              >
                {isUnmet ? `! ${stem}` : stem}
                <span
                  data-testid="repeated-pick-count"
                  style={{ fontSize: '11px', fontWeight: 400 }}
                >
                  {limitText}
                </span>
              </span>
              <button
                type="button"
                style={{ ...actionStyle(canUndo), marginLeft: 'auto' }}
                disabled={!canUndo}
                onClick={onUndo}
              >
                Undo
              </button>
            </div>

            <div
              style={{
                ...styles.numberRow,
                gridTemplateColumns: gridColumns(unitCount),
              }}
            >
              {groups.map((_, unit) => (
                // eslint-disable-next-line react/no-array-index-key
                <span key={unit}>{unit + 1}</span>
              ))}
            </div>

            {options.map((option) => {
              const is86ed = option.available === false;
              const allOn = isRowAllOn(option.itemId);
              return (
                <div key={option.itemId} data-testid="repeated-pick-row">
                  <div style={styles.nameLine}>
                    <span
                      style={{
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: is86ed ? Colors.Thunderbird : undefined,
                        textDecoration: is86ed ? 'line-through' : undefined,
                      }}
                    >
                      {option.name}
                    </span>
                    {!is86ed && (
                      <span
                        role="button"
                        tabIndex={-1}
                        aria-pressed={allOn}
                        data-testid="repeated-pick-all"
                        data-option={option.itemId}
                        style={{ ...allToggleStyle(allOn), ...readOnlyStyle }}
                        onClick={() => onAllClick(option)}
                      >
                        all
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      ...styles.cells,
                      gridTemplateColumns: gridColumns(unitCount),
                    }}
                  >
                    {groups.map((_, unit) => {
                      const on = previewOn(unit, option.itemId);
                      return (
                        <div
                          // eslint-disable-next-line react/no-array-index-key
                          key={unit}
                          role="checkbox"
                          aria-checked={on}
                          aria-disabled={is86ed}
                          title={`${unit + 1}: ${option.name}`}
                          data-testid="repeated-pick-cell"
                          data-option={option.itemId}
                          data-unit={unit}
                          style={{
                            ...cellStyle(on, flashingUnits.includes(unit)),
                            ...(is86ed ? { cursor: 'default' } : null),
                            ...readOnlyStyle,
                          }}
                          onPointerDown={(event) =>
                            onCellPointerDown(unit, option.itemId, event)
                          }
                          onPointerEnter={() =>
                            onCellPointerEnter(unit, option.itemId)
                          }
                        >
                          {on ? '✓' : ''}
                        </div>
                      );
                    })}
                  </div>
                  {renderFollowUps(option.itemId)}
                </div>
              );
            })}

            <div
              style={{
                ...styles.countRow,
                gridTemplateColumns: gridColumns(unitCount),
              }}
            >
              {picks.map((unitPicks, unit) => (
                <span
                  // eslint-disable-next-line react/no-array-index-key
                  key={unit}
                  data-testid="repeated-pick-unit-count"
                  style={{
                    color: isUnitShort(unit)
                      ? Colors.Thunderbird
                      : Colors.silverChalice,
                  }}
                >
                  {unitPicks.length}
                </span>
              ))}
            </div>
          </>
        )}
        {isCollapsed && followUpsByOption.size > 0 && (
          // Folded, the follow-ups are hidden, not unmounted: their defaults
          // still apply and the item still validates.
          <div style={HIDDEN_WHEN_CLOSED} data-testid="repeated-pick-hidden">
            {Array.from(followUpsByOption.keys()).map((optionId) =>
              renderFollowUps(optionId)
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RepeatedPickGrid;
